/**
 * Recruiter Check — what the job card's grade chip opens.
 *
 * Two render paths live here on purpose:
 *
 *  • GRADED (Recruiter Check, BR-6). The stored `output_quality` carries only a
 *    compact summary (grade, verdict, per-check counts), so the full findings
 *    are fetched from `POST /jobs/{jobid}/quality-recheck` when the lightbox
 *    opens — the record never bloats and the list is always current. Layout:
 *    verdict banner → "Fix once — master resume" → "Fix for this job", each
 *    card saying WHERE / WHAT / WHY / HOW with buttons that take the user
 *    there.
 *
 *  • LEGACY. Every resume rendered before Recruiter Check has no `grade`, so it
 *    keeps the original quality window unchanged, plus a "Run Recruiter Check"
 *    button that fetches a grade on demand.
 *
 * Honesty rule (BR-6): the grade describes the RENDERED files. Fixing the
 * master resume does not improve an already-rendered PDF, so master findings
 * are framed "fixed in master — Regenerate to update this resume". Nothing
 * here ever recomputes a friendlier grade client-side.
 *
 * PORTALED to document.body on purpose (LEARNINGS: a `fixed inset-0` overlay
 * rendered inside a card positions against that card, not the viewport — and
 * JobCard carries a `scale-*` transform).
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Gauge, X, Loader2, RefreshCw, PencilLine, FileEdit, Sparkles, AlertTriangle,
} from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { api } from '../services/api';
import {
  citationsSentence,
  gradeBannerClasses,
  gradeChipClasses,
  gradeLabel,
  qualityCheckedAt,
  qualityChipClasses,
  qualityCountChips,
  qualityIsClean,
  qualityLabel,
} from '../utils/outputQuality';
import { SUBCOMPONENTS } from '../types';
import type {
  EditorFocusTarget, OutputQuality, RecruiterAction, RecruiterCheckResponse,
  RecruiterFinding, RecruiterGrade, SubcomponentType,
} from '../types';

interface Props {
  isOpen: boolean;
  quality: OutputQuality;
  /** Needed to fetch findings. Absent → the stored summary is all we can show. */
  jobid?: string;
  /** Card the resume was generated for — names the window so a user with
   *  several windows' worth of cards knows which one they opened. */
  jobcompany?: string;
  jobtitle?: string;
  onClose: () => void;
  /**
   * Open the master resume in Edit Resume (where "fix once" findings live).
   * The target deep-links the editor to the finding's section/item; a host that
   * ignores it still gets the old behaviour (editor opens at the top).
   */
  onOpenResumeEditor?: (target?: Omit<EditorFocusTarget, 'nonce'>) => void;
  /** Open this job's section editor for one generated section. */
  onEditSection?: (component: SubcomponentType) => void;
  /** Re-render this job's files — closes the master-fix loop. */
  onRegenerate?: () => void;
  /**
   * Start over for this job: regenerate EVERY section, then the resume and
   * cover letter files. This is what the master-resume findings mean by
   * "Regenerate" — `onRegenerate` above only re-renders the files from the
   * sections that already exist.
   */
  onRegenerateAll?: () => void;
}

/** Severity dot, same vocabulary the Scorecard uses. */
const SEV_DOT: Record<RecruiterFinding['severity'], string> = {
  high: 'bg-red-500', med: 'bg-amber-500', low: 'bg-gray-400',
};

const GROUP_TITLES: Record<RecruiterFinding['group'], string> = {
  master: 'Fix once — master resume',
  job: 'Fix for this job',
};

const GROUP_BLURBS: Record<RecruiterFinding['group'], string> = {
  master: 'These come from your master resume, so fixing one fixes every resume you generate from it. This resume shows the fix after you Regenerate.',
  job: 'These are about how this particular resume reads against this particular job.',
};

/**
 * What an action button should DO. The backend owns the labels; the kind is
 * matched loosely so a naming variant ("open_edit_resume", "edit_master") still
 * lands on the right handler. An action we can't honour is dropped — never
 * rendered as a button that does nothing.
 */
type ActionKind = 'master' | 'section' | 'regenerate' | null;

export function resolveActionKind(action: RecruiterAction): ActionKind {
  const kind = (action.kind || '').toLowerCase();
  if (kind.includes('regen')) return 'regenerate';
  if (kind.includes('section') || kind.includes('subcomponent')) return 'section';
  if (kind.includes('master') || kind.includes('resume') || kind.includes('dedicated')) return 'master';
  return null;
}

/** The section an 'edit section' action targets, when it names a real one. */
export function actionSection(action: RecruiterAction): SubcomponentType | null {
  const target = (action.target || '').toLowerCase().trim();
  return (SUBCOMPONENTS as readonly string[]).includes(target)
    ? (target as SubcomponentType)
    : null;
}

/**
 * The one-click "start over" action, shown in BOTH views' STICKY FOOTER bar.
 *
 * Footer, not body, on purpose: it first shipped inside the `overflow-y-auto`
 * body under the findings, where it was visible for the ~1s the list was
 * loading and then pushed below the fold once 10-20 finding cards rendered
 * above it — the button looked like it disappeared. The footer bar is outside
 * the scroll container, so it stays put however long the list gets.
 *
 * Deliberately separate from the per-finding action buttons and from the
 * missing-file "Regenerate this resume" fallback — those stay exactly as they
 * were. Renders nothing when the host didn't wire the handler. No helper text:
 * footer space is tight, so the tooltip carries the explanation.
 */
export function RegenerateAllButton({
  onRegenerateAll, onClose,
}: { onRegenerateAll?: () => void; onClose: () => void }) {
  if (!onRegenerateAll) return null;
  return (
    <button
      type="button"
      data-testid="recruiter-regenerate-all"
      title="Regenerates every section and the resume + cover letter files for this job"
      onClick={() => { onRegenerateAll(); onClose(); }}
      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
    >
      <RefreshCw className="w-4 h-4" /> Regenerate Now
    </button>
  );
}

export function QualityModal({
  isOpen, quality, jobid, jobcompany, jobtitle, onClose,
  onOpenResumeEditor, onEditSection, onRegenerate, onRegenerateAll,
}: Props) {
  useEscapeClose(onClose, isOpen);

  // Findings fetched from the recheck endpoint. `null` = not fetched yet.
  const [result, setResult] = useState<RecruiterCheckResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  /** Master findings the user has gone off to fix — badged with the regen hint. */
  const [openedInMaster, setOpenedInMaster] = useState<Set<string>>(new Set());

  const runCheck = useCallback(async () => {
    if (!jobid) return;
    setLoading(true);
    setFetchFailed(false);
    try {
      setResult(await api.recheckQuality(jobid));
    } catch {
      // A failed fetch is NOT an empty result: keep whatever the record says
      // and offer a retry, rather than implying the resume is clean.
      setFetchFailed(true);
    } finally {
      setLoading(false);
    }
  }, [jobid]);

  // Graded card: fetch on every open, so the list reflects the resume as it
  // stands right now. Legacy cards wait for the user to press the button.
  useEffect(() => {
    if (!isOpen) {
      setResult(null);
      setFetchFailed(false);
      setOpenedInMaster(new Set());
      return;
    }
    if (quality.grade && jobid) void runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, jobid, quality.grade]);

  if (!isOpen) return null;

  const cites = citationsSentence(quality);
  const checked = qualityCheckedAt(quality);
  const subject = [jobcompany, jobtitle].filter(Boolean).join(' — ');
  // The response wins once it lands (it is fresher than the stored summary);
  // until then the record's own grade holds the banner so nothing flickers.
  const noFiles = result?.status === 'no_files';
  const grade: RecruiterGrade | undefined =
    (!noFiles && (result?.grade as RecruiterGrade | undefined)) || quality.grade;
  const verdictLine = (!noFiles && result?.verdict_line) || quality.verdict || '';
  const findings: RecruiterFinding[] = (!noFiles && result?.findings) || [];
  const impression = (!noFiles && result?.impression) || quality.impression || '';
  const impressionAvailable = result ? result.impression_available !== false : true;
  const graded = !!grade;

  const masterFindings = findings.filter((f) => f.group === 'master');
  const jobFindings = findings.filter((f) => f.group !== 'master');

  function renderActions(f: RecruiterFinding) {
    const buttons = (f.actions || [])
      .map((a) => ({ action: a, kind: resolveActionKind(a) }))
      .filter(({ kind, action }) => {
        if (kind === 'master') return !!onOpenResumeEditor;
        if (kind === 'section') return !!onEditSection && !!actionSection(action);
        if (kind === 'regenerate') return !!onRegenerate;
        return false;
      });
    if (!buttons.length) return null;
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        {buttons.map(({ action, kind }, i) => (
          <button
            key={`${action.kind}-${i}`}
            type="button"
            data-testid="recruiter-finding-action"
            data-kind={kind || ''}
            onClick={() => {
              if (kind === 'master') {
                setOpenedInMaster((prev) => new Set(prev).add(f.id));
                onOpenResumeEditor?.({
                  path: f.path || action.target || null,
                  field: f.field,
                  snippet: f.snippet,
                });
                onClose();
              } else if (kind === 'section') {
                const section = actionSection(action);
                if (section) {
                  onEditSection?.(section);
                  onClose();
                }
              } else if (kind === 'regenerate') {
                onRegenerate?.();
                onClose();
              }
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary-700 border border-primary-300 rounded hover:bg-primary-50"
          >
            {kind === 'master' && <PencilLine className="w-3.5 h-3.5" />}
            {kind === 'section' && <FileEdit className="w-3.5 h-3.5" />}
            {kind === 'regenerate' && <RefreshCw className="w-3.5 h-3.5" />}
            {action.label || (kind === 'master'
              ? 'Open in Edit Resume'
              : kind === 'section' ? 'Edit this section' : 'Regenerate')}
          </button>
        ))}
      </div>
    );
  }

  function renderFinding(f: RecruiterFinding) {
    const fixedInMaster = f.group === 'master' && openedInMaster.has(f.id);
    return (
      <div
        key={f.id}
        className="rounded-lg border border-gray-200 bg-white p-3 space-y-1.5"
        data-testid="recruiter-finding"
        data-check={f.check}
        data-group={f.group}
        data-severity={f.severity}
      >
        <div className="flex items-start gap-2">
          <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${SEV_DOT[f.severity] || SEV_DOT.low}`} />
          <div className="min-w-0 flex-1">
            {f.where && (
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 break-words">
                {f.where}
              </p>
            )}
            {f.what && <p className="text-sm text-gray-800 leading-relaxed">{f.what}</p>}
            {f.snippet && (
              <p className="text-xs text-gray-500 italic mt-1 break-words [overflow-wrap:anywhere]">
                “{f.snippet}”
              </p>
            )}
            {f.why && <p className="text-xs text-gray-600 mt-1 leading-relaxed">{f.why}</p>}
            {f.how && (
              <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                <span className="font-medium">What to do: </span>{f.how}
              </p>
            )}
            {fixedInMaster && (
              <p
                className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                data-testid="recruiter-master-regen-hint"
              >
                Fixed in your master resume? This resume still shows the old text —
                <span className="font-medium"> Regenerate</span> to update it.
              </p>
            )}
            {renderActions(f)}
          </div>
        </div>
      </div>
    );
  }

  function renderGroup(group: RecruiterFinding['group'], list: RecruiterFinding[]) {
    if (!list.length) return null;
    return (
      <div className="space-y-2" data-testid={`recruiter-group-${group}`}>
        <div>
          <h3 className="text-sm font-semibold text-gray-800">
            {GROUP_TITLES[group]}
            <span className="ml-1.5 text-xs font-normal text-gray-500">({list.length})</span>
          </h3>
          <p className="text-[11px] text-gray-500 leading-relaxed">{GROUP_BLURBS[group]}</p>
        </div>
        {list.map(renderFinding)}
      </div>
    );
  }

  // ── Recruiter Check view ────────────────────────────────────────────────
  const gradedBody = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Recruiter Check"
      data-testid="recruiter-check-modal"
      data-grade={grade || ''}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-4 border-b">
          <div className="flex items-start gap-2">
            <Gauge className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold">Recruiter Check</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {subject || 'How a recruiter reads the resume built for this job.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-4">
          {/* Verdict banner */}
          {grade && (
            <div className={`rounded-lg border p-3 ${gradeBannerClasses(grade)}`}>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded text-sm font-bold ${gradeChipClasses(grade)}`}>
                  {gradeLabel(grade)}
                </span>
                {verdictLine && <span className="text-sm font-medium">{verdictLine}</span>}
              </div>
              {impression ? (
                <p className="text-sm mt-2 leading-relaxed" data-testid="recruiter-impression">
                  {impression}
                </p>
              ) : impressionAvailable ? null : (
                <p className="text-xs mt-2 opacity-90" data-testid="recruiter-impression-hint">
                  A recruiter&apos;s 10-second first impression is part of an active plan — every
                  check above and below is yours either way.
                </p>
              )}
            </div>
          )}

          {noFiles && (
            <div
              className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
              data-testid="recruiter-no-files"
            >
              <p className="font-medium">Nothing to check yet.</p>
              <p className="mt-1 leading-relaxed">
                {result?.message
                  || 'Recruiter Check reads the resume file this job actually produced, and this one is no longer available. Regenerate this resume to run the check.'}
              </p>
              {onRegenerate && (
                <button
                  type="button"
                  onClick={() => { onRegenerate(); onClose(); }}
                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary-700 border border-primary-300 rounded hover:bg-primary-50"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Regenerate this resume
                </button>
              )}
            </div>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="recruiter-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              Checking this resume against the job…
            </div>
          )}

          {fetchFailed && (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
              data-testid="recruiter-fetch-error"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p>
                    Couldn&apos;t load the full check just now. Below is the summary saved when
                    this resume was generated.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {qualityCountChips(quality).map((c) => (
                      <span
                        key={c.key}
                        className={`px-2 py-0.5 rounded border text-[11px] font-medium ${c.classes}`}
                      >
                        {c.label}: {c.count}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => void runCheck()}
                    data-testid="recruiter-retry"
                    className="mt-2 px-2.5 py-1 text-xs font-medium text-amber-900 border border-amber-300 rounded hover:bg-amber-100"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {renderGroup('master', masterFindings)}
          {renderGroup('job', jobFindings)}

          {!loading && !fetchFailed && !noFiles && result && findings.length === 0 && (
            <p className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              Nothing is holding this one back — no killer issues found.
            </p>
          )}

          <p className="text-[11px] text-gray-400 leading-relaxed">
            This grades the resume that was actually generated for this job — a guide, not a gate.
            You can always apply. Regenerate after editing your resume to grade it again.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t">
          <span className="text-xs text-gray-500">{checked || ''}</span>
          <div className="flex items-center gap-2">
            <RegenerateAllButton onRegenerateAll={onRegenerateAll} onClose={onClose} />
            {jobid && !loading && (
              <button
                type="button"
                onClick={() => void runCheck()}
                data-testid="recruiter-recheck"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Re-check
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Legacy view (payload with no grade) ─────────────────────────────────
  const legacyBody = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Generated resume quality"
      data-testid="quality-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-4 border-b">
          <div className="flex items-start gap-2">
            <Gauge className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold">Generated resume quality</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {subject || 'How the resume built for this job reads.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold text-gray-800 leading-none">{quality.score}</span>
            <span className="text-lg text-gray-400 leading-none">/100</span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${qualityChipClasses(quality.band)}`}>
              {qualityLabel(quality)}
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {qualityCountChips(quality).map((c) => (
              <span
                key={c.key}
                className={`px-2 py-0.5 rounded border text-[11px] font-medium ${c.classes}`}
              >
                {c.label}: {c.count}
              </span>
            ))}
          </div>

          {quality.top.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700">Worth a look</h3>
              {quality.top.map((t, i) => (
                <p
                  key={i}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 leading-relaxed"
                >
                  {t}
                </p>
              ))}
            </div>
          )}

          {cites && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 leading-relaxed">
              {cites}
            </p>
          )}

          {qualityIsClean(quality) && (
            <p className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              Nothing flagged on this one.
            </p>
          )}

          {/* Recruiter Check on demand: this resume was rendered before the
              grade existed, so nothing is stored — one call computes it from
              the files that are already on S3. */}
          {jobid && (
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-3">
              <p className="text-sm text-primary-900 leading-relaxed">
                Want the recruiter&apos;s view? Recruiter Check grades this resume against this
                job and names exactly what a recruiter would trip over.
              </p>
              {fetchFailed && (
                <p className="text-xs text-red-700 mt-1" data-testid="recruiter-legacy-error">
                  Couldn&apos;t run the check just now — please try again.
                </p>
              )}
              {noFiles && (
                <p className="text-xs text-primary-800 mt-1" data-testid="recruiter-legacy-no-files">
                  {result?.message
                    || 'Recruiter Check reads the resume file this job produced, and this one is no longer available. Regenerate this resume to run the check.'}
                </p>
              )}
              <button
                type="button"
                onClick={() => void runCheck()}
                disabled={loading}
                data-testid="run-recruiter-check"
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {loading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Sparkles className="w-3.5 h-3.5" />}
                {loading ? 'Checking…' : 'Run Recruiter Check'}
              </button>
            </div>
          )}

          <p className="text-[11px] text-gray-400 leading-relaxed">
            This grades the resume that was actually generated for this job — a guide, not a gate.
            Regenerate after editing your resume to score it again.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t">
          <span className="text-xs text-gray-500">{checked || ''}</span>
          <div className="flex items-center gap-2">
            <RegenerateAllButton onRegenerateAll={onRegenerateAll} onClose={onClose} />
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const body = graded ? gradedBody : legacyBody;

  return createPortal(body, document.body);
}

export default QualityModal;
